import axios, {
  AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { VITE_API_ENDPOINT } from "@/config/constants";
import { CustomNotifyService } from "@/services/CustomNotifyService";

export default defineNuxtPlugin(() => {
  const auth = async () => (await import("@/services/AuthService")).default;

  const notifySrv = new CustomNotifyService();

  const appTokenRouteBlacklist = ["/oauth/token"];

  const SSR = () => import.meta.env.SSR === true;

  const LOGIN_ROUTE = "/eshop/login";

  const clientTokenRouteBlacklist = [
    LOGIN_ROUTE,
    "/eshop/register",
    "/shop-v1/customers/-actions/self",
  ];

  let checkedCustomerToken = false;

  const requestApplyToken = async (
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    config.headers["Accept"] = "application/vnd.api+json";
    config.headers["Content-Type"] = "application/vnd.api+json";
    if (!appTokenRouteBlacklist.find((route) => route == config.url)) {
      if (!SSR()) {
        const token = (await auth()).getToken();
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
      }
    }
    return config;
  };

  const requestInterceptor = async (
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    await requestApplyToken(config);
    return config;
  };

  const sucessResponseInterceptor = async (response: AxiosResponse) => {
    // Permitir mostrar notificación de desconexión posterior al iniciar sesión
    if (response.status === 200 && response.config.url === LOGIN_ROUTE) {
      checkedCustomerToken = false;
    }
    return response;
  };

  const responseErrorInterceptor = async (error: AxiosError) => {
    if (!(error instanceof AxiosError)) {
      notifySrv.getNotification({
        type: "error",
        title: "Error inesperado",
        text: error,
      });
    }
    const originalRequest: InternalAxiosRequestConfig | undefined =
      error.config;
    // Get the token after the request gets 401 and try again.
    if (
      error.response?.status === 401 &&
      !clientTokenRouteBlacklist.find((route) => route == originalRequest?.url)
    ) {
      if (!checkedCustomerToken) {
        if (!SSR()) {
          (await auth()).clearSessionStorage();
        }
        checkedCustomerToken = true;
        console.log(
          "check token",
          await (await auth()).isCustomerTokenExpired(),
        );
        if (!SSR() && (await (await auth()).isCustomerTokenExpired())) {
          notifySrv.getNotification({
            type: "error",
            title: "Sesión expirada",
            text: "Tu sesión ha expirado, vuelve a iniciar sesión si así lo deseas.",
            time: 3000,
          });
          return Promise.reject(error);
        } else {
          notifySrv.getNotification({
            type: "error",
            title: "Sesión requerida",
            text:
              "Necesitas iniciar sesión para continuar en esta pantalla. " +
              originalRequest?.url,
            time: 3000,
          });
          return Promise.reject(error);
        }
      }
    }
    if (error.status === 429) {
      notifySrv.getNotification({
        type: "error",
        title: "Intentos de reconexión excedidos",
        text: "Por favor espera un poco y vuelve a recargar la página.",
      });
    } else if (error.code && error.code === "ERR_NETWORK") {
      notifySrv.getNotification({
        type: "error",
        title: "Sin respuesta del servidor",
        text: "Por favor verfica la conexión del servidor y luego recarga esta página.",
      });
    } else if (error.response && error.response.data) {
      const message = (error.response.data as { message: string | null })
        .message;
      if (message && message.includes("SQLSTATE[HY000] [2002]")) {
        notifySrv.getNotification({
          type: "error",
          title: "Conexión denegada.",
          text: "Problema técnico con la base de datos de servidor, espera un poco y vuelve a recargar la página.",
        });
      }
    }
    return Promise.reject(error);
  };

  axios.defaults.baseURL = VITE_API_ENDPOINT;
  axios.interceptors.request.use(requestInterceptor);
  axios.interceptors.response.use(
    sucessResponseInterceptor,
    responseErrorInterceptor,
  );

  return {
    provide: {
      axios,
    },
  };
});
